import React from 'react';

interface LoadingScreenProps {
  message?: string;
  showLogo?: boolean;
}

const LoadingScreen: React.FC<LoadingScreenProps> = ({ 
  message = 'Loading...', 
  showLogo = true 
}) => {
  return (
    <div className="min-h-screen w-full flex items-center justify-center relative overflow-hidden bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      {/* Animated Background - Same as Login */}
      <div className="absolute inset-0">
        {/* Animated Orbs */}
        <div className="absolute -top-20 -left-20 w-96 h-96 bg-purple-300 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-blob-1"></div>
        <div className="absolute top-0 right-0 w-96 h-96 bg-blue-300 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-blob-2"></div>
        <div className="absolute bottom-0 left-10 w-96 h-96 bg-pink-300 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-blob-3"></div>
        <div className="absolute top-1/3 left-1/2 w-96 h-96 bg-indigo-300 rounded-full mix-blend-multiply filter blur-3xl opacity-25 animate-blob-4"></div>
        <div className="absolute bottom-10 right-20 w-80 h-80 bg-purple-400 rounded-full mix-blend-multiply filter blur-3xl opacity-25 animate-blob-5"></div>
      </div>

      {/* Loading Card */}
      <div className="relative z-10 w-full max-w-md mx-auto px-6">
        {/* Glassmorphism Card */}
        <div className="relative backdrop-blur-3xl bg-white/25 border border-white/40 rounded-3xl shadow-2xl shadow-indigo-200/50 p-12 transform transition-all duration-300 before:absolute before:inset-0 before:rounded-3xl before:bg-gradient-to-br before:from-white/40 before:to-white/10 before:pointer-events-none">
          <div className="relative z-10 flex flex-col items-center">
            {/* Logo Section */}
            {showLogo && (
              <div className="relative mb-8 animate-float">
                <div className="absolute inset-0 bg-gradient-to-r from-purple-400 to-blue-500 rounded-2xl blur-xl opacity-40 animate-pulse"></div>
                <div className="relative bg-white/90 backdrop-blur-xl p-4 rounded-2xl border border-white/60 shadow-lg">
                  <img 
                    src="https://webfiles.remapp.ae/web_remapp_ae/hjas-1-1.png" 
                    alt="REM Clients Logo" 
                    className="h-16 w-auto" 
                  />
                </div>
              </div>
            )}

            {/* Loading Spinner */}
            <div className="relative mb-6">
              {/* Outer spinning ring */}
              <div className="w-20 h-20 rounded-full border-4 border-indigo-200/30 border-t-indigo-600 border-r-purple-600 animate-spin-slow"></div>
              {/* Inner pulsing circle */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-12 h-12 rounded-full bg-gradient-to-r from-indigo-500 to-purple-500 animate-pulse-scale"></div>
              </div>
              {/* Center icon */}
              <div className="absolute inset-0 flex items-center justify-center">
                <svg className="w-6 h-6 text-white animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
            </div>

            {/* Loading Text */}
            <div className="text-center space-y-2">
              <h2 className="text-2xl font-bold text-gray-900 tracking-tight drop-shadow-sm animate-fade-in">
                {message}
              </h2>
              <div className="flex items-center justify-center gap-1">
                <div className="w-2 h-2 bg-indigo-600 rounded-full animate-bounce animation-delay-0"></div>
                <div className="w-2 h-2 bg-purple-600 rounded-full animate-bounce animation-delay-200"></div>
                <div className="w-2 h-2 bg-indigo-600 rounded-full animate-bounce animation-delay-400"></div>
              </div>
            </div>

            {/* Optional hint text */}
            <p className="mt-6 text-indigo-700 text-sm font-medium drop-shadow-sm text-center animate-fade-in">
              Please wait a moment...
            </p>
          </div>
        </div>

        {/* Decorative Elements */}
        <div className="absolute -top-6 -left-6 w-32 h-32 bg-gradient-to-r from-purple-400 to-pink-400 rounded-full filter blur-3xl opacity-20 animate-pulse"></div>
        <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-gradient-to-r from-blue-400 to-indigo-400 rounded-full filter blur-3xl opacity-20 animate-pulse animation-delay-2000"></div>
      </div>

      {/* Custom Animations - Same as Login */}
      <style>{`
        @keyframes blob1 {
          0%, 100% { transform: translate(0px, 0px) scale(1); }
          25% { transform: translate(400px, 200px) scale(1.2); }
          50% { transform: translate(200px, 400px) scale(0.9); }
          75% { transform: translate(-100px, 300px) scale(1.1); }
        }
        @keyframes blob2 {
          0%, 100% { transform: translate(0px, 0px) scale(1); }
          25% { transform: translate(-300px, 150px) scale(0.9); }
          50% { transform: translate(-500px, 350px) scale(1.15); }
          75% { transform: translate(-200px, 500px) scale(1); }
        }
        @keyframes blob3 {
          0%, 100% { transform: translate(0px, 0px) scale(1); }
          25% { transform: translate(300px, -200px) scale(1.1); }
          50% { transform: translate(600px, -100px) scale(0.95); }
          75% { transform: translate(150px, -300px) scale(1.05); }
        }
        @keyframes blob4 {
          0%, 100% { transform: translate(0px, 0px) scale(1); }
          25% { transform: translate(-400px, -250px) scale(1.15); }
          50% { transform: translate(-100px, -400px) scale(0.9); }
          75% { transform: translate(-300px, -150px) scale(1.1); }
        }
        @keyframes blob5 {
          0%, 100% { transform: translate(0px, 0px) scale(1); }
          25% { transform: translate(250px, 300px) scale(0.95); }
          50% { transform: translate(-200px, 200px) scale(1.2); }
          75% { transform: translate(100px, -150px) scale(1); }
        }
        .animate-blob-1 {
          animation: blob1 20s ease-in-out infinite;
        }
        .animate-blob-2 {
          animation: blob2 18s ease-in-out infinite;
          animation-delay: -5s;
        }
        .animate-blob-3 {
          animation: blob3 22s ease-in-out infinite;
          animation-delay: -10s;
        }
        .animate-blob-4 {
          animation: blob4 25s ease-in-out infinite;
          animation-delay: -15s;
        }
        .animate-blob-5 {
          animation: blob5 19s ease-in-out infinite;
          animation-delay: -8s;
        }
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
        }
        .animate-float {
          animation: float 3s ease-in-out infinite;
        }
        @keyframes spin-slow {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .animate-spin-slow {
          animation: spin-slow 2s linear infinite;
        }
        @keyframes pulse-scale {
          0%, 100% { transform: scale(1); opacity: 0.8; }
          50% { transform: scale(1.1); opacity: 1; }
        }
        .animate-pulse-scale {
          animation: pulse-scale 2s ease-in-out infinite;
        }
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fade-in 0.6s ease-out;
        }
        .animation-delay-0 {
          animation-delay: 0ms;
        }
        .animation-delay-200 {
          animation-delay: 200ms;
        }
        .animation-delay-400 {
          animation-delay: 400ms;
        }
        .animation-delay-2000 {
          animation-delay: 2s;
        }
      `}</style>
    </div>
  );
};

export default LoadingScreen;

